import {
  IonButton,
  IonCard,
  IonCardContent,
  IonContent,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonLoading,
  IonNote,
  IonPage,
  useIonAlert,
  useIonRouter,
  useIonToast,
  useIonViewWillEnter,
} from "@ionic/react";
import { businessOutline } from "ionicons/icons";
import ApiOptionsModal from "../components/ApiOptionsModal";
import { useContext, useState } from "react";
import { initializeAbpSession } from "../api/abpLogin";
import { getUserSetups } from "../api/UserSetups";
import { withAITracking } from "@microsoft/applicationinsights-react-js";
import { reactPlugin } from "../util/AppInsights";
import PageHeader from "../components/PageHeader";
import {
  TranslationMessagesContext,
  TranslationsContext,
} from "../util/Translations";
import { getUserSettings } from "../util/UserHelper";
import { clearSiteData } from "../util/ApiOptionsHelper";
import OfflineToggle from "../components/OfflineToggle";
import BiometricToggle from "../components/BiometricToggle";
import {
  isBiometricAvailable,
  isBiometricEnabled,
  setBiometricEnabled,
} from "../util/Biometric";
import { ThemeContext } from "../util/ThemeManager";
import { ThemePreference } from "../models/common/ThemePreference";
import {
  MessagingSettings,
  SETTINGS_ROOT,
  readMessagingSettings,
  useEamSettingsReadOnly,
} from "./settings/settingsShared";
import { getNotificationSummary } from "./settings/NotificationSettings";
import {
  ModuleDefaults,
  readModuleDefaults,
} from "./settings/ModuleDefaultsSettings";
import "./settings/Settings.css";

const Settings: React.FC = () => {
  const { translations } = useContext(TranslationsContext);
  const { translatedMessages } = useContext(TranslationMessagesContext);
  const { themePreference } = useContext(ThemeContext);
  const [presentToast] = useIonToast();
  const [presentAlert] = useIonAlert();
  const router = useIonRouter();
  const readOnly = useEamSettingsReadOnly();
  const [isOpen, setIsOpen] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [plant, setPlant] = useState<string>();
  const [database, setDatabase] = useState<string>();
  const [messagingInstalled, setMessagingInstalled] = useState(false);
  const [messaging, setMessaging] = useState<MessagingSettings>();
  const [language, setLanguage] = useState<string>();
  const [moduleDefaults, setModuleDefaults] = useState<ModuleDefaults>();
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricActive, setBiometricActive] = useState(false);

  // Refresh summaries each time Settings is shown, including on return from a
  // sub-page, so changes made there are reflected here.
  useIonViewWillEnter(() => {
    (async () => {
      const userSettings = await getUserSettings();
      setPlant(userSettings?.SessionSettingList?.Plant);
      setDatabase(userSettings?.SessionSettingList?.Database);
      const installed = !!userSettings?.ModulesInstalled?.Messaging;
      setMessagingInstalled(installed);

      const { available } = await isBiometricAvailable();
      setBiometricAvailable(available);
      setBiometricActive(available && (await isBiometricEnabled()));

      if (readOnly) return; // EAM values cannot be read offline; keep the last ones
      if (installed) readMessagingSettings().then(setMessaging).catch(() => undefined);
      readModuleDefaults().then(setModuleDefaults);
      getUserSetups({ userSection: "options", userParameter: "language" }).then(
        (response) => !response?.isError && setLanguage(response.data?.UserValue)
      );
    })();
  }, [readOnly]);

  const themeLabel =
    themePreference === ThemePreference.dark
      ? translations["lbl_dark"] || "Dark"
      : themePreference === ThemePreference.light
      ? translations["lbl_light"] || "Light"
      : translations["lbl_system_default"] || "System Default";

  const moduleDefaultsSummary = moduleDefaults
    ? Object.values(moduleDefaults).filter(Boolean).join(", ") ||
      translations["lbl_mob_no_query_set"] ||
      "No query set"
    : "";

  const signOutAndRemoveBiometric = async () => {
    // Deliberate full sign-out: forget biometric first so the logout flow does
    // not re-persist it, then run a normal logout.
    await setBiometricEnabled(false);
    globalThis.relogin = false;
    router.push("/logout");
  };

  const confirmApiOptions = async (cancel: boolean, relogin?: boolean) => {
    setIsOpen(false);
    if (cancel) {
      presentToast({
        message:
          translatedMessages["NoChangesDBPlant"]?.MessageText ||
          "No changes to database or plant",
        duration: 1000,
        position: "top",
      });
      return;
    }
    setShowLoading(true);
    if (relogin) {
      setShowLoading(false);
      globalThis.relogin = true;
      router.push("/logout");
      return;
    }
    await initializeAbpSession();
    setShowLoading(false);
    //redirect to home
    window.location.href = "/home";
  };

  const clearCache = async () => {
    setShowLoading(true);
    // finish clearing before the reload so nothing stale is served afterwards
    await clearSiteData();
    presentToast({
      message:
        translatedMessages["CacheCleared"]?.MessageText || "Cache cleared",
      duration: 1000,
      position: "top",
    });
    await confirmApiOptions(false);
  };

  const confirmClearCache = () => {
    presentAlert({
      header: translations["lbl_mob_clear_cache_title"] || "Clear cache?",
      message:
        translations["lbl_mob_clear_cache_message"] ||
        "Cached data is removed and the app reloads to the home screen.",
      buttons: [
        { text: translations["lbl_btn_cancel"] || "Cancel", role: "cancel" },
        {
          text: translations["lbl_mob_clear"] || "Clear",
          role: "destructive",
          handler: () => {
            clearCache();
          },
        },
      ],
    });
  };

  const sectionTitle = (key: string, fallback: string) => (
    <div className="settings-section-title">{translations[key] || fallback}</div>
  );

  const navRow = (
    href: string,
    labelKey: string,
    label: string,
    summary?: string
  ) => (
    <IonItem button detail routerLink={`${SETTINGS_ROOT}/${href}`}>
      <IonLabel>{translations[labelKey] || label}</IonLabel>
      {summary && (
        <IonNote slot="end" className="settings-summary">
          {summary}
        </IonNote>
      )}
    </IonItem>
  );

  return (
    <IonPage>
      <PageHeader title={translations["lbl_settings"] || "Settings"} />
      <IonContent fullscreen className="settings-content">
        <IonLoading
          isOpen={showLoading}
          onDidDismiss={() => setShowLoading(false)}
          message={`${
            translations["lbl_refreshing_data"] || "Refreshing data..."
          }:`}
          duration={5000}
        />
        <div className="settings-container">
          {/* Plant and database */}
          <IonCard className="settings-plant-card">
            <IonCardContent>
              <IonIcon icon={businessOutline} className="settings-plant-icon" />
              <div className="settings-plant-text">
                <div className="settings-plant-label">
                  {translations["lbl_mob_plant_database"] || "Plant and database"}
                </div>
                <div className="settings-plant-name">{plant ?? "—"}</div>
                <div className="settings-plant-db">{database ?? "—"}</div>
              </div>
              <IonButton
                fill="outline"
                size="small"
                onClick={() => setIsOpen(true)}
              >
                {translations["lbl_mob_change"] || "Change"}
              </IonButton>
            </IonCardContent>
          </IonCard>

          {sectionTitle("lbl_mob_settings_offline", "Offline")}
          <IonList inset className="settings-list">
            <OfflineToggle />
          </IonList>

          {sectionTitle("lbl_mob_settings_preferences", "Preferences")}
          <IonList inset className="settings-list">
            {messagingInstalled &&
              navRow(
                "notifications",
                "lbl_mob_settings_notifications",
                "Notifications",
                messaging ? getNotificationSummary(messaging, translations) : ""
              )}
            {navRow(
              "general",
              "lbl_mob_settings_general",
              "General",
              [themeLabel, language].filter(Boolean).join(" · ")
            )}
            {navRow(
              "module-defaults",
              "lbl_mob_settings_module_defaults",
              "Module defaults",
              moduleDefaultsSummary
            )}
          </IonList>

          {biometricAvailable && (
            <>
              {sectionTitle("lbl_mob_settings_security", "Sign-in and security")}
              <IonList inset className="settings-list">
                <BiometricToggle />
                {biometricActive && (
                  <IonItem
                    button
                    className="settings-destructive"
                    onClick={() => signOutAndRemoveBiometric()}
                  >
                    <IonLabel color="danger">
                      {translations["lbl_signout_remove_biometric"] ||
                        "Sign out and remove biometric sign-in"}
                    </IonLabel>
                  </IonItem>
                )}
              </IonList>
            </>
          )}

          {sectionTitle("lbl_mob_settings_storage", "Storage")}
          <IonList inset className="settings-list">
            <IonItem button detail={false} onClick={confirmClearCache}>
              <IonLabel className="ion-text-wrap">
                <h2>{translations["lbl_clear_ cache"] || "Clear Cache"}</h2>
                <p>
                  {translations["lbl_mob_clear_cache_help"] ||
                    "Removes cached data and reloads the app."}
                </p>
              </IonLabel>
            </IonItem>
          </IonList>
        </div>
        <ApiOptionsModal
          isOpen={isOpen}
          isInit={false}
          confirmCallback={confirmApiOptions}
        />
      </IonContent>
    </IonPage>
  );
};

export default withAITracking(reactPlugin, Settings, "Settings");
